import { useEffect, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";

import firebase from "../../firebase";

export default function ResetPasswordDialog(props) {
    const [email, setEmail] = useState("");
    const [inProgress, setInProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (props.open) {
            setEmail(props.email || "");
            setInProgress(false);
            setErrorMessage("");
        }
    }, [props.email, props.open]);

    const handleClose = () => {
        if (!inProgress) {
            props.onClose();
        }
    };

    function handleResetClick(event) {
        event.preventDefault();

        const trimmedEmail = email.trim();
        if (!trimmedEmail || inProgress) {
            return;
        }

        setInProgress(true);
        setErrorMessage("");

        const requestPasswordReset = firebase
            .functions()
            .httpsCallable("requestPasswordReset");

        requestPasswordReset({
            email: trimmedEmail,
            variant: "reset",
        })
            .then(() => {
                setInProgress(false);
                props.onClose();
            })
            .catch((error) => {
                console.error("Could not send password reset email", error);
                setErrorMessage(
                    "We could not send the reset email. Check the address and try again.",
                );
                setInProgress(false);
            });
    }

    return (
        <Dialog
            onClose={handleClose}
            aria-labelledby="reset-password-dialog-title"
            open={props.open}
            fullWidth
            maxWidth="xs"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <LockResetOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="reset-password-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Reset password
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, lineHeight: 1.55 }}
                    >
                        We’ll email you a secure link to choose a new password.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close password reset"
                    onClick={handleClose}
                    disabled={inProgress}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>

                {inProgress && (
                    <LinearProgress
                        aria-label="Sending password reset email"
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}
                    />
                )}
            </Box>

            <Box component="form" noValidate onSubmit={handleResetClick}>
                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, sm: 3 },
                        backgroundColor: "background.default",
                    }}
                >
                    {errorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Alert>
                    )}

                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, px: 0.5, fontWeight: 800 }}
                    >
                        Account email
                    </Typography>
                    <TextField
                        id="reset-password-email"
                        label="Email address"
                        type="email"
                        variant="outlined"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        autoComplete="email"
                        autoFocus
                        required
                        fullWidth
                        disabled={inProgress}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailOutlinedIcon
                                        color="action"
                                        fontSize="small"
                                    />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                minHeight: 54,
                                borderRadius: 2,
                                backgroundColor: "background.paper",
                            },
                        }}
                    />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1, px: 0.5 }}
                    >
                        Use the email address linked to your Nodiant account.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Button
                        color="inherit"
                        onClick={handleClose}
                        disabled={inProgress}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disableElevation
                        disabled={!email.trim() || inProgress}
                        sx={{
                            minWidth: 126,
                            fontFamily: "DraigRegular",
                            fontSize: 16,
                            letterSpacing: "0.02em",
                        }}
                    >
                        {inProgress ? "Sending…" : "Send reset link"}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
}
